import { useField } from 'formik';
import alertIconSrc from 'images/icons/alert_orange.svg';
import errorIconSrc from 'images/icons/input_error.svg';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { InputFieldUIStates } from 'services/forms/forms.config';
import { inputFieldStateReducer } from 'services/forms/forms.helpers';
import { FormGroup, Input, LabelInput } from 'style/styleInput.style';
import useGetComponentRenderUniqueId from 'utils/hooks/useGetComponentRenderUniqueId';
import {
  useComputeInputFieldHandleChange,
  useTrackFieldInputFieldEvents,
} from './InputField.hooks';
import { InputFieldProps } from './InputField.types';
import {
  ContainerMessage,
  DefaultMessage,
  IconInsideInput,
  MessageError,
} from './style/input.style';

const InputField = function InputField(
  {
    label,
    placeholder,
    isPresentMessage,
    isPresentIconInsideInput = false,
    id,
    tracingId,
    tracingIdError,
    customMessaggeId,
    customMessageData,
    hidden,
    transformError,
    className,
    spellCheck = false,
    autoCorrect = 'off',
    inputComponent: InputComponent = Input,
    filterValue,
    autoComplete = 'new-password',
    maxLength,
    disabled,
    additionalInfoId,
    isFieldErrorAlert,
    additionalInfoData,
    inputComponentExtraProps,
    analyticsName,
    hasOnlyNotesBelow = false,
    forceAlertAsError = false,
    errorLabelHeight,
    padding,
    paddingWithNotes,
    onChangeCallback,
    onBlurCallback,
    onFocusCallback,
    ...otherProps
  }: InputFieldProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const [field, meta, formikHelpers] = useField(otherProps);
  const { required, type = 'text' } = otherProps;
  const {
    onChange: formikOnChangeNotUsedDirectly,
    onBlur: formikOnBlur,
    ...fieldProps
  } = field;
  const compId = useGetComponentRenderUniqueId('counter');
  const domId = id || tracingId || compId;

  const { inputFieldState, messageProps } = inputFieldStateReducer(
    meta,
    isFieldErrorAlert,
    transformError,
    additionalInfoId,
    additionalInfoData,
  );

  const adjustedInputFieldState =
    forceAlertAsError && inputFieldState === InputFieldUIStates.alert
      ? InputFieldUIStates.error
      : inputFieldState;

  const handleChange = useComputeInputFieldHandleChange(
    field,
    formikHelpers,
    filterValue,
    onChangeCallback,
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      formikOnBlur(e);
      onBlurCallback?.(e);
    },
    [formikOnBlur, onBlurCallback],
  );

  useTrackFieldInputFieldEvents(
    adjustedInputFieldState,
    messageProps,
    analyticsName,
  );

  const iconSrc =
    adjustedInputFieldState === InputFieldUIStates.alert
      ? alertIconSrc
      : errorIconSrc;

  return (
    <div hidden={hidden} className={className}>
      <FormGroup
        isAlert={adjustedInputFieldState === InputFieldUIStates.alert}
        isError={adjustedInputFieldState === InputFieldUIStates.error}
      >
        <InputComponent
          placeholder={placeholder}
          type={type}
          required={required}
          id={domId}
          autoCorrect={autoCorrect}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          maxLength={maxLength}
          disabled={disabled}
          onFocus={onFocusCallback}
          ref={ref}
          {...fieldProps}
          {...inputComponentExtraProps}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {label && (
          <LabelInput htmlFor={domId}>
            <Translate id={label} />
          </LabelInput>
        )}
        {isPresentIconInsideInput && <IconInsideInput iconSrc={iconSrc} />}
      </FormGroup>
      <ContainerMessage
        isPresentMessage={isPresentMessage}
        hasOnlyNotesBelow={hasOnlyNotesBelow}
        height={errorLabelHeight}
        padding={padding}
        paddingWithNotes={paddingWithNotes}
      >
        <MessageError id={tracingIdError}>
          {!!messageProps && <Translate {...messageProps} />}
        </MessageError>

        {!!customMessageData && !messageProps && (
          <DefaultMessage>
            <Translate id={customMessageData} />
          </DefaultMessage>
        )}
      </ContainerMessage>
    </div>
  );
};

export default React.memo(React.forwardRef(InputField));
